<template>
  <ul
    v-if="mappedTravels.length"
    class="journey-list"
  >
    <li
      v-for="(travel, travelIndex) in mappedTravels"
      :key="travelIndex"
      class="journey-list__list"
    >
      <JourneySelectionHandler
        :travel="travel"
        :travel-index="travelIndex"
        :is-mobile="isMobile"
        :is-rebooking="isRebooking"
        :show-outbound="false"
        @bundle-selected="handleBundleSelection"
        @update-route-codes="handleRouteCodesUpdate"
        @open-details-drawer="openDetailsDrawer"
      />
    </li>
  </ul>
  <div v-else class="search-result__no-results">
    <p>{{ $t('searchResult.journey.noTravels') }}</p>
    <p v-if="travels.length && !mappedTravels.length" class="journey-list__info">
      {{ $t('common.loading') }}...
    </p>
  </div>

  <JourneyListMobile
    v-if="isMobile && !isRebooking"
    :selected-route="selectedRoute"
    :selected-travel="selectedTravel"
    :selected-bundle="selectedBundle"
    :selected-travel-index="selectedTravelIndex"
    :selected-route-index="selectedRouteIndex"
    :is-details-drawer-open="isDetailsDrawerOpen"
    :is-tickets-drawer-open="isTicketsDrawerOpen"
    :is-outbound="false"
    :return-travels="[]"
    :selected-return-travel="null"
    :show-inbound-travels="() => {}"
    :is-rebooking="isRebooking"
    :items-to-replace="itemsToReplace"
    @update-route-codes="handleRouteCodesUpdate"
    @bundle-selected="handleBundleSelection"
    @close-details="closeDetailsDrawer"
    @close-tickets="closeTicketsDrawer"
    @open-tickets="openTicketsDrawer"
  />
</template>

<script lang="ts">
  import { defineComponent, PropType, ref, onMounted, computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Travel, Route } from '@/types/travelTypes'
  import { SearchService, SearchSelection } from '@/types/searchTypes'
  import { useJourneyStore } from '@/stores/journeyStore'
  import JourneySelectionHandler from './JourneySelectionHandler.vue'
  import JourneyListMobile from './JourneyListMobile.vue'
  import { useTravelMapping } from './composables/useTravelMapping'

  export default defineComponent({
    name: 'InboundJourneyList',
    components: {
      JourneySelectionHandler,
      JourneyListMobile,
    },
    props: {
      travels: {
        type: Array as PropType<Travel[]>,
        required: true,
      },
      services: {
        type: Array as PropType<SearchService[]>,
        required: false,
      },
      isRebooking: {
        type: Boolean,
        default: false,
      },
      itemsToReplace: {
        type: Array as PropType<string[]>,
        default: () => [],
      },
    },
    emits: ['bundle-selected'],
    setup(props, { emit }) {
      const { t } = useI18n()
      const journeyStore = useJourneyStore()
      const { mappedTravels, updateRouteCodes } = useTravelMapping(props)
      const selectedBundle = ref<{ bundleIndex: number } | null>(null)
      const isDetailsDrawerOpen = ref(false)
      const isTicketsDrawerOpen = ref(false)
      const selectedTravelIndex = ref<number>(-1)
      const selectedRouteIndex = ref<number>(-1)
      const selectedRoute = ref<Route | null>(null)
      const selectedTravel = ref<Travel | null>(null)
      const isMobile = ref(window.innerWidth <= 440)
      
      const openDetailsDrawer = ({
        travelIndex,
        routeIndex,
      }: Pick<SearchSelection, 'travelIndex' | 'routeIndex'>) => {
        selectedTravelIndex.value = travelIndex
        selectedRouteIndex.value = routeIndex
        const travel = mappedTravels.value[travelIndex]
        selectedTravel.value = travel
        
        if (travel && travel.routes && travel.routes.length > routeIndex) {
          selectedRoute.value = travel.routes[routeIndex]
        } else {
          selectedRoute.value = null
        }
        isDetailsDrawerOpen.value = true
      }

      const closeDetailsDrawer = () => {
        isDetailsDrawerOpen.value = false
        selectedBundle.value = null
      }

      const openTicketsDrawer = () => {
        isDetailsDrawerOpen.value = false
        isTicketsDrawerOpen.value = true
        selectedBundle.value = null
      }

      const closeTicketsDrawer = () => {
        isTicketsDrawerOpen.value = false
        selectedBundle.value = null
      }

      const handleBundleSelection = (selection: SearchSelection) => {
        selectedBundle.value = { bundleIndex: selection.bundleIndex }
        emit('bundle-selected', selection)
      }

      const handleRouteCodesUpdate = ({
        travelIndex,
        routeIndex,
        routeCodes,
      }: {
        travelIndex: number
        routeIndex: number
        routeCodes: string[]
      }) => {
        updateRouteCodes(travelIndex, routeIndex, routeCodes)
      }

      const handleResize = () => {
        isMobile.value = window.innerWidth <= 440
      }

      onMounted(() => {
        window.addEventListener('resize', handleResize)
      })

      return {
        mappedTravels,
        selectedBundle,
        isDetailsDrawerOpen,
        isTicketsDrawerOpen,
        selectedTravelIndex,
        selectedRouteIndex,
        selectedRoute,
        selectedTravel,
        isMobile,
        openDetailsDrawer,
        closeDetailsDrawer,
        openTicketsDrawer,
        closeTicketsDrawer,
        handleBundleSelection,
        handleRouteCodesUpdate,
        isRebooking: props.isRebooking,
        itemsToReplace: props.itemsToReplace,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .journey-list {
    list-style-type: none;
    margin: 0;
    padding: 0;
  }
</style>
